import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ProductDetail } from "@/mocks/productDetails";

const CATEGORY_SEARCH_MAP: Record<string, string[]> = {
  PS5: ["بلايستيشن 5", "ps5", "playstation 5"],
  PS4: ["بلايستيشن 4", "ps4", "playstation 4"],
  PC: ["كمبيوتر", "pc", "gaming pc", "ألعاب"],
  Controllers: ["يد تحكم", "controllers", "جويستيك", "يد", "dualsense", "dualshock"],
  Headsets: ["سماعات", "headsets", "سماعة", "pulse"],
};

const CATEGORY_LABELS: Record<string, string> = {
  PS5: "بلايستيشن 5",
  PS4: "بلايستيشن 4",
  PC: "كمبيوتر ألعاب",
  Controllers: "أيادي تحكم",
  Headsets: "سماعات",
};

interface SearchSuggestionsProps {
  query: string;
  products: ProductDetail[];
  onSelect: (value: string) => void;
  onClose: () => void;
}

interface SuggestionItem {
  type: "product" | "category" | "keyword";
  label: string;
  sublabel?: string;
  image?: string;
  value: string;
  productId?: number;
}

function buildSuggestions(query: string, products: ProductDetail[]): SuggestionItem[] {
  const q = query.trim().toLowerCase();
  if (!q || q.length < 1) return [];

  const suggestions: SuggestionItem[] = [];
  const seen = new Set<string>();

  // 1. Product matches (max 5)
  const matchedProducts = products.filter((p) => {
    const catExtra = (CATEGORY_SEARCH_MAP[p.category] ?? []).join(" ");
    return (
      p.nameAr.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      catExtra.toLowerCase().includes(q) ||
      (p.shortDesc ?? "").toLowerCase().includes(q) ||
      (p.badge ?? "").toLowerCase().includes(q)
    );
  }).slice(0, 5);

  matchedProducts.forEach((p) => {
    suggestions.push({
      type: "product",
      label: p.nameAr,
      sublabel: CATEGORY_LABELS[p.category] ?? p.category,
      image: p.images?.[0],
      value: p.nameAr,
      productId: p.id,
    });
    seen.add(p.nameAr.toLowerCase());
  });

  // 2. Category matches
  Object.entries(CATEGORY_SEARCH_MAP).forEach(([cat, keywords]) => {
    const matches = keywords.some((k) => k.toLowerCase().includes(q)) || cat.toLowerCase().includes(q);
    if (matches && !seen.has(cat.toLowerCase())) {
      seen.add(cat.toLowerCase());
      suggestions.push({
        type: "category",
        label: CATEGORY_LABELS[cat] ?? cat,
        sublabel: `تصفح فئة ${CATEGORY_LABELS[cat] ?? cat}`,
        value: CATEGORY_LABELS[cat] ?? cat,
      });
    }
  });

  // 3. Keyword completions from product names
  const allWords = products.flatMap((p) => p.nameAr.split(" "));
  const uniqueWords = [...new Set(allWords)];
  uniqueWords
    .filter((w) => w.toLowerCase().startsWith(q) && w.length > q.length && !seen.has(w.toLowerCase()))
    .slice(0, 3)
    .forEach((w) => {
      seen.add(w.toLowerCase());
      suggestions.push({
        type: "keyword",
        label: w,
        value: w,
      });
    });

  return suggestions.slice(0, 8);
}

export default function SearchSuggestions({ query, products, onSelect, onClose }: SearchSuggestionsProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestions = buildSuggestions(query, products);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  if (!query.trim() || suggestions.length === 0) return null;

  const handleClick = (item: SuggestionItem) => {
    if (item.type === "product" && item.productId) {
      navigate(`/product/${item.productId}`);
      onClose();
    } else {
      onSelect(item.value);
      onClose();
    }
  };

  const highlightMatch = (text: string, query: string) => {
    const q = query.trim();
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="text-amber-400 font-bold">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className="absolute top-full right-0 left-0 mt-2 bg-[#141414] border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl"
      style={{ fontFamily: "'Cairo', sans-serif" }}
    >
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
        <span className="text-gray-500 text-xs">اقتراحات البحث</span>
        <span className="text-gray-600 text-xs">{suggestions.length} نتيجة</span>
      </div>

      {/* Suggestions List */}
      <ul>
        {suggestions.map((item, idx) => (
          <li key={idx}>
            <button
              onMouseDown={(e) => { e.preventDefault(); handleClick(item); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors cursor-pointer text-right group"
            >
              {/* Icon / Image */}
              <div className="w-9 h-9 flex items-center justify-center shrink-0">
                {item.type === "product" && item.image ? (
                  <img
                    src={item.image}
                    alt={item.label}
                    className="w-9 h-9 object-cover rounded-lg"
                  />
                ) : item.type === "category" ? (
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-400/10 rounded-lg">
                    <i className="ri-apps-line text-amber-400 text-base"></i>
                  </div>
                ) : (
                  <div className="w-9 h-9 flex items-center justify-center bg-white/5 rounded-lg">
                    <i className="ri-search-line text-gray-500 text-base"></i>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0 text-right">
                <p className="text-white text-sm leading-tight truncate">
                  {highlightMatch(item.label, query)}
                </p>
                {item.sublabel && (
                  <p className="text-gray-500 text-xs mt-0.5 truncate">{item.sublabel}</p>
                )}
              </div>

              {/* Arrow */}
              <div className="w-5 h-5 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {item.type === "product" ? (
                  <i className="ri-arrow-left-line text-gray-500 text-sm"></i>
                ) : (
                  <i className="ri-corner-down-left-line text-gray-500 text-sm"></i>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-white/5 flex items-center gap-3">
        <span className="text-gray-600 text-xs flex items-center gap-1">
          <i className="ri-corner-down-left-line text-xs"></i>
          للبحث
        </span>
        <span className="text-gray-600 text-xs flex items-center gap-1">
          <i className="ri-arrow-up-down-line text-xs"></i>
          للتنقل
        </span>
        <span className="text-gray-600 text-xs flex items-center gap-1">
          <i className="ri-close-line text-xs"></i>
          للإغلاق
        </span>
      </div>
    </div>
  );
}
